"use client";

//
// MakeCreateTab.tsx — Web counterpart of iOS `CreateView` in `MakePage.swift`.
//
// Mirrors the SwiftUI structure:
//   - createModePicker (DSUnderlineTabBar: simple / custom)
//   - stylesSection (DSGlassCard: header + GlassTextEditor + tag chips)
//   - lyricsSection (DSGlassCard: header + instrumental toggle + editor + lyrics generate button)
//   - simplePromptSuggestionsSection (4 cards in simple mode)
//   - advancedOptionsSection (collapsible: vocal gender UnderlineTabBar + negative prompt editor)
//   - titleSection (DSGlassCard with GlassTextField)
//   - bottomActionBar (sticky: Generate Music button + model selector)
//
// All colors resolve through DS CSS variables (`var(--ds-*)`); no inline hex
// except for the model call-to-action tints which mirror iOS model catalog.
//

import { type CSSProperties, useEffect, useMemo, useState } from "react";
import {
	ArtistIcon,
	GlassButton,
	GlassCard,
	GlassIconButton,
	GlassSwitch,
	GlassTag,
	GlassTextEditor,
	GlassTextField,
	UnderlineTabBar,
} from "@/components/design-system/ArtistDesignSystem";
import {
	CREATE_MODE_LABELS,
	type CreateComposerMode,
	type CreateVocalGender,
	type MakeModelDescriptor,
	type PromptSuggestion,
	sampleStyleTags,
	VOCAL_GENDER_LABELS,
} from "./makeCatalog";
import { loadDesignerPromptSuggestions } from "@/domain/designer/DesignerContentRepository";
import { useTranslation } from "@/i18n/ArtistI18n";

export interface CreateComposerDraft {
	mode: CreateComposerMode;
	isSimpleLyricsExpanded: boolean;
	lyrics: string;
	isInstrumental: boolean;
	styles: string;
	negativePrompt: string;
	title: string;
	vocalGender: CreateVocalGender;
	isAdvancedExpanded: boolean;
}

export function initialCreateDraft(): CreateComposerDraft {
	return {
		mode: "simple",
		isSimpleLyricsExpanded: false,
		lyrics: "",
		isInstrumental: false,
		styles: "",
		negativePrompt: "",
		title: "",
		vocalGender: "none",
		isAdvancedExpanded: false,
	};
}

function hasAnyInput(draft: CreateComposerDraft): boolean {
	return (
		draft.lyrics.trim().length > 0 ||
		draft.styles.trim().length > 0 ||
		draft.negativePrompt.trim().length > 0 ||
		draft.title.trim().length > 0
	);
}

function hasVisibleLyricsOrStylesInput(
	draft: CreateComposerDraft,
	model: MakeModelDescriptor | undefined,
): boolean {
	const supportsCustomMode = model?.supportsCustomMode ?? true;
	return (
		draft.styles.trim().length > 0 ||
		(supportsCustomMode && draft.lyrics.trim().length > 0)
	);
}

export interface MakeCreateTabProps {
	readonly draft: CreateComposerDraft;
	readonly onDraftChange: (next: CreateComposerDraft) => void;
	readonly selectedModel: MakeModelDescriptor | undefined;
	readonly availableModels: readonly MakeModelDescriptor[];
	readonly onSelectModel: (model: MakeModelDescriptor) => void;
	readonly onClearDraft: () => void;
}

export function MakeCreateTab({
	draft,
	onDraftChange,
	selectedModel,
	availableModels,
	onSelectModel,
	onClearDraft,
}: Readonly<MakeCreateTabProps>) {
	const { resolvedLocale } = useTranslation();
	const supportsCustomMode = selectedModel?.supportsCustomMode ?? true;
	const supportsInstrumentalToggle =
		selectedModel?.supportsInstrumentalToggle ?? false;
	const supportsVocalGenderControl =
		selectedModel?.supportsVocalGenderControl ?? false;
	const supportsLyricsGeneration =
		selectedModel?.supportsLyricsGeneration ?? false;
	const isLyricsGenerationAvailable =
		supportsCustomMode && !draft.isInstrumental && supportsLyricsGeneration;

	const [suggestedStyleTags, setSuggestedStyleTags] = useState<string[]>([]);
	const [isSubmittingGeneration, setIsSubmittingGeneration] = useState(false);
	const [isSubmittingLyrics, setIsSubmittingLyrics] = useState(false);
	const [isModelSheetOpen, setIsModelSheetOpen] = useState(false);
	const [promptSuggestions, setPromptSuggestions] = useState<
		readonly PromptSuggestion[] | null
	>(null);
	const [promptSuggestionsFailed, setPromptSuggestionsFailed] = useState(false);
	const [promptSuggestionsReload, setPromptSuggestionsReload] = useState(0);

	useEffect(() => {
		if (suggestedStyleTags.length === 0) {
			setSuggestedStyleTags(sampleStyleTags(10));
		}
	}, [suggestedStyleTags.length]);

	useEffect(() => {
		let active = true;
		const requestGeneration = promptSuggestionsReload;
		setPromptSuggestions(null);
		setPromptSuggestionsFailed(false);
		loadDesignerPromptSuggestions(resolvedLocale)
			.then((items) => {
				if (active && requestGeneration === promptSuggestionsReload) {
					setPromptSuggestions(items);
				}
			})
			.catch(() => {
				if (active && requestGeneration === promptSuggestionsReload) {
					setPromptSuggestionsFailed(true);
				}
			});
		return () => {
			active = false;
		};
	}, [promptSuggestionsReload, resolvedLocale]);

	useEffect(() => {
		if (!supportsCustomMode && draft.mode === "custom") {
			onDraftChange({
				...draft,
				mode: "simple",
				isSimpleLyricsExpanded: false,
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [supportsCustomMode, draft.mode, onDraftChange, draft]);

	useEffect(() => {
		if (!supportsInstrumentalToggle && draft.isInstrumental) {
			onDraftChange({ ...draft, isInstrumental: false });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [supportsInstrumentalToggle, draft.isInstrumental, onDraftChange, draft]);

	useEffect(() => {
		if (!supportsVocalGenderControl && draft.vocalGender !== "none") {
			onDraftChange({ ...draft, vocalGender: "none" });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [supportsVocalGenderControl, onDraftChange, draft.vocalGender, draft]);

	const usesCompactLyricsLayout = useMemo(() => {
		if (!supportsCustomMode) return true;
		if (draft.isInstrumental) return true;
		return (
			draft.mode === "simple" &&
			!draft.isSimpleLyricsExpanded &&
			draft.lyrics.trim().length === 0
		);
	}, [
		supportsCustomMode,
		draft.isInstrumental,
		draft.mode,
		draft.isSimpleLyricsExpanded,
		draft.lyrics,
	]);

	const canSubmitGeneration =
		selectedModel !== undefined &&
		(draft.styles.trim().length > 0 ||
			(supportsCustomMode &&
				!draft.isInstrumental &&
				draft.lyrics.trim().length > 0)) &&
		!isSubmittingGeneration &&
		!isSubmittingLyrics;

	function applyStyleTag(tag: string) {
		const trimmed = draft.styles.trim();
		const next = trimmed.length === 0 ? tag : `${trimmed}, ${tag}`;
		onDraftChange({ ...draft, styles: next });
	}

	function applyPromptSuggestion(suggestion: PromptSuggestion) {
		onDraftChange({ ...draft, styles: suggestion.prompt });
	}

	function handleSubmitMusic() {
		if (!canSubmitGeneration) return;
		setIsSubmittingGeneration(true);
		window.setTimeout(() => setIsSubmittingGeneration(false), 1200);
	}

	function handleSubmitLyrics() {
		if (!isLyricsGenerationAvailable || isSubmittingLyrics) return;
		setIsSubmittingLyrics(true);
		window.setTimeout(() => setIsSubmittingLyrics(false), 1200);
	}

	const showGenerateBar = hasVisibleLyricsOrStylesInput(draft, selectedModel);

	return (
		<div className="make-create-tab">
			<div className="make-create-tab__mode-picker">
				<UnderlineTabBar
					tabs={
						supportsCustomMode
							? (["simple", "custom"] as const)
							: (["simple"] as const)
					}
					selected={draft.mode}
					onSelect={(value) => {
						const nextMode = value as CreateComposerMode;
						const next: CreateComposerDraft = { ...draft, mode: nextMode };
						if (nextMode === "simple") {
							next.isAdvancedExpanded = false;
							if (next.lyrics.trim().length === 0) {
								next.isSimpleLyricsExpanded = false;
							}
						}
						if (nextMode === "custom") {
							next.isSimpleLyricsExpanded = true;
						}
						onDraftChange(next);
					}}
					label={(value) => CREATE_MODE_LABELS[value as CreateComposerMode]}
				/>
			</div>

			<form
				className="make-create-tab__form"
				aria-label="创作表单"
				onSubmit={(event) => {
					event.preventDefault();
					handleSubmitMusic();
				}}
			>
				{draft.mode === "simple" ? (
					<>
						<StylesSection
							draft={draft}
							onDraftChange={onDraftChange}
							suggestedStyleTags={suggestedStyleTags}
							onRefreshTags={() => setSuggestedStyleTags(sampleStyleTags(10))}
							onApplyTag={applyStyleTag}
						/>
						<LyricsSection
							draft={draft}
							onDraftChange={onDraftChange}
							selectedModel={selectedModel}
							usesCompactLyricsLayout={usesCompactLyricsLayout}
							isLyricsGenerationAvailable={isLyricsGenerationAvailable}
							isSubmittingLyrics={isSubmittingLyrics}
							onSubmitLyrics={handleSubmitLyrics}
						/>
						<PromptSuggestionsSection
							suggestions={promptSuggestions}
							failed={promptSuggestionsFailed}
							onRetry={() => setPromptSuggestionsReload((value) => value + 1)}
							onSelect={applyPromptSuggestion}
						/>
					</>
				) : (
					<>
						<LyricsSection
							draft={draft}
							onDraftChange={onDraftChange}
							selectedModel={selectedModel}
							usesCompactLyricsLayout={usesCompactLyricsLayout}
							isLyricsGenerationAvailable={isLyricsGenerationAvailable}
							isSubmittingLyrics={isSubmittingLyrics}
							onSubmitLyrics={handleSubmitLyrics}
						/>
						<StylesSection
							draft={draft}
							onDraftChange={onDraftChange}
							suggestedStyleTags={suggestedStyleTags}
							onRefreshTags={() => setSuggestedStyleTags(sampleStyleTags(10))}
							onApplyTag={applyStyleTag}
						/>
						<AdvancedOptionsSection draft={draft} onDraftChange={onDraftChange} />
						<TitleSection draft={draft} onDraftChange={onDraftChange} />
					</>
				)}

				{/* Hidden submit so Enter inside text fields can trigger generation */}
				<button
					type="submit"
					aria-hidden="true"
					tabIndex={-1}
					style={visuallyHidden}
				/>
			</form>

			<section
			className="make-create-tab__bottom-bar"
			aria-label="生成操作"
		>
				{showGenerateBar ? (
					<div className="make-create-tab__bottom-row">
						<GlassButton
							kind="primary"
							size="lg"
							expands
							isLoading={isSubmittingGeneration}
							isEnabled={canSubmitGeneration}
							symbol={<ArtistIcon name="sparkle" size={16} />}
							onClick={handleSubmitMusic}
						>
							{selectedModel
								? `生成音乐 · ${selectedModel.musicGenerationPrice} 灵感`
								: "生成音乐"}
						</GlassButton>
						<ModelSelectorButton
							selectedModel={selectedModel}
							availableModels={availableModels}
							onSelectModel={onSelectModel}
							isSheetOpen={isModelSheetOpen}
							onSheetToggle={() => setIsModelSheetOpen((open) => !open)}
							onSheetClose={() => setIsModelSheetOpen(false)}
						/>
					</div>
				) : (
					<div className="make-create-tab__bottom-row make-create-tab__bottom-row--centered">
						<ModelSelectorButton
							selectedModel={selectedModel}
							availableModels={availableModels}
							onSelectModel={onSelectModel}
							isSheetOpen={isModelSheetOpen}
							onSheetToggle={() => setIsModelSheetOpen((open) => !open)}
							onSheetClose={() => setIsModelSheetOpen(false)}
						/>
					</div>
				)}
				{hasAnyInput(draft) ? (
				<button
					type="button"
					className="make-create-tab__clear"
					onClick={onClearDraft}
				>
					清空草稿
				</button>
			) : null}
		</section>
	</div>
	);
}

// ─────────────────────────────────────────────────────────────────────────────
// Styles Section
// ─────────────────────────────────────────────────────────────────────────────

interface StylesSectionProps {
	readonly draft: CreateComposerDraft;
	readonly onDraftChange: (next: CreateComposerDraft) => void;
	readonly suggestedStyleTags: readonly string[];
	readonly onRefreshTags: () => void;
	readonly onApplyTag: (tag: string) => void;
}

function StylesSection({
	draft,
	onDraftChange,
	suggestedStyleTags,
	onRefreshTags,
	onApplyTag,
}: Readonly<StylesSectionProps>) {
	const heading = draft.mode === "simple" ? "描述你想要的歌" : "风格";
	const placeholder =
		draft.mode === "simple"
			? "例如：合成器流行 + 女声 + 怀旧"
			: "synthwave, dream pop, female vocal...";
	return (
		<GlassCard className="make-create-section">
			<header className="make-create-section__header">
				<h2>{heading}</h2>
			</header>
			<GlassTextEditor
				label={heading}
				placeholder={placeholder}
				value={draft.styles}
				onChange={(event) =>
					onDraftChange({ ...draft, styles: event.target.value })
				}
				minLength={0}
			/>
			<div className="make-create-section__tags">
				<GlassIconButton
					label="刷新风格标签"
					kind="neutral"
					type="button"
					onClick={onRefreshTags}
				>
					<ArtistIcon name="sliders" size={18} />
				</GlassIconButton>
				<ul className="make-create-section__tag-list" aria-label="推荐风格标签">
					{suggestedStyleTags.map((tag) => (
						<li key={tag}>
							<button
								type="button"
								className="make-create-section__tag-button"
								onClick={() => onApplyTag(tag)}
							>
								<GlassTag kind="neutral">{tag}</GlassTag>
							</button>
						</li>
					))}
				</ul>
			</div>
		</GlassCard>
	);
}

// ─────────────────────────────────────────────────────────────────────────────
// Lyrics Section
// ─────────────────────────────────────────────────────────────────────────────

interface LyricsSectionProps {
	readonly draft: CreateComposerDraft;
	readonly onDraftChange: (next: CreateComposerDraft) => void;
	readonly selectedModel: MakeModelDescriptor | undefined;
	readonly usesCompactLyricsLayout: boolean;
	readonly isLyricsGenerationAvailable: boolean;
	readonly isSubmittingLyrics: boolean;
	readonly onSubmitLyrics: () => void;
}

function LyricsSection({
	draft,
	onDraftChange,
	selectedModel,
	usesCompactLyricsLayout,
	isLyricsGenerationAvailable,
	isSubmittingLyrics,
	onSubmitLyrics,
}: Readonly<LyricsSectionProps>) {
	const supportsCustomMode = selectedModel?.supportsCustomMode ?? true;
	const supportsInstrumentalToggle =
		selectedModel?.supportsInstrumentalToggle ?? false;
	const lyricsDisabled = draft.isInstrumental || !supportsCustomMode;
	const collapseLabel = usesCompactLyricsLayout ? "添加歌词" : "收起歌词";

	return (
		<GlassCard className="make-create-section">
			<header className="make-create-section__header make-create-section__header--lyrics">
				<div className="make-create-section__lyrics-title">
					{draft.mode === "simple" ? (
						<button
							type="button"
							className="make-create-section__expand"
							aria-label={collapseLabel}
							aria-expanded={!usesCompactLyricsLayout}
							disabled={lyricsDisabled}
							onClick={() =>
								onDraftChange({
									...draft,
									isSimpleLyricsExpanded: !draft.isSimpleLyricsExpanded,
								})
							}
						>
							<ArtistIcon
								name={usesCompactLyricsLayout ? "plus" : "close"}
								size={14}
								strokeWidth={2.4}
							/>
						</button>
					) : null}
					<h2>歌词</h2>
				</div>
				<div className="make-create-section__instrumental">
					<span className="make-create-section__instrumental-label">
						纯音乐
					</span>
					<GlassSwitch
						checked={draft.isInstrumental}
						onChange={(checked) => {
							const next: CreateComposerDraft = {
								...draft,
								isInstrumental: checked,
							};
							if (checked) next.vocalGender = "none";
							onDraftChange(next);
						}}
						label="纯音乐"
						disabled={!supportsInstrumentalToggle}
					/>
				</div>
			</header>

			{usesCompactLyricsLayout ? (
				<p className="make-create-section__lyrics-hint">
					{lyricsDisabled
						? draft.isInstrumental
							? "已切换为纯音乐，无歌词。"
							: "当前模型不支持自定义歌词。"
						: "点按左侧加号添加自定义歌词，或留空让模型自动生成。"}
				</p>
			) : (
				<div className="make-create-section__lyrics-body">
					<GlassTextEditor
						label="歌词"
						placeholder="输入歌词，或留空让模型自动生成..."
						value={draft.lyrics}
						onChange={(event) =>
							onDraftChange({ ...draft, lyrics: event.target.value })
						}
						disabled={lyricsDisabled}
					/>
					<div className="make-create-section__lyrics-actions">
						<GlassButton
							kind="secondary"
							size="sm"
							isLoading={isSubmittingLyrics}
							isEnabled={isLyricsGenerationAvailable && !isSubmittingLyrics}
							symbol={<ArtistIcon name="sparkle" size={14} />}
							onClick={onSubmitLyrics}
						>
							{selectedModel
								? `生成歌词 · ${selectedModel.lyricsGenerationPrice} 灵感`
								: "生成歌词"}
						</GlassButton>
					</div>
				</div>
			)}
		</GlassCard>
	);
}

// ─────────────────────────────────────────────────────────────────────────────
// Prompt Suggestions Section
// ─────────────────────────────────────────────────────────────────────────────

interface PromptSuggestionsSectionProps {
	readonly suggestions: readonly PromptSuggestion[] | null;
	readonly failed: boolean;
	readonly onRetry: () => void;
	readonly onSelect: (suggestion: PromptSuggestion) => void;
}

function PromptSuggestionsSection({
	suggestions,
	failed,
	onRetry,
	onSelect,
}: Readonly<PromptSuggestionsSectionProps>) {
	const { t } = useTranslation();
	return (
		<section className="make-create-suggestions" aria-label={t("make.suggestions.title")}>
			<header className="make-create-suggestions__header">
				<h2>{t("make.suggestions.title")}</h2>
				<span>{t("make.suggestions.subtitle")}</span>
			</header>
			{failed ? (
				<div className="make-create-suggestions__status" role="alert">
					<span>{t("make.suggestions.error")}</span>
					<button type="button" onClick={onRetry}>{t("common.retry")}</button>
				</div>
			) : suggestions === null ? (
				<div className="make-create-suggestions__status" role="status">
					{t("make.suggestions.loading")}
				</div>
			) : suggestions.length === 0 ? (
				<div className="make-create-suggestions__status">
					{t("make.suggestions.empty")}
				</div>
			) : (
				<ul className="make-create-suggestions__grid">
					{suggestions.map((suggestion) => (
						<li key={suggestion.id}>
							<button
								type="button"
								className="make-create-suggestions__card"
								onClick={() => onSelect(suggestion)}
							>
								<img src={suggestion.imageURL} alt="" loading="lazy" />
								<span className="make-create-suggestions__copy">
									<strong className="make-create-suggestions__caption">
										{suggestion.caption}
									</strong>
									<span className="make-create-suggestions__prompt">
										{suggestion.prompt}
									</span>
								</span>
							</button>
						</li>
					))}
				</ul>
			)}
		</section>
	);
}

// ─────────────────────────────────────────────────────────────────────────────
// Advanced Options Section
// ─────────────────────────────────────────────────────────────────────────────

interface AdvancedOptionsSectionProps {
	readonly draft: CreateComposerDraft;
	readonly onDraftChange: (next: CreateComposerDraft) => void;
}

function AdvancedOptionsSection({
	draft,
	onDraftChange,
}: Readonly<AdvancedOptionsSectionProps>) {
	return (
		<GlassCard
			className="make-create-section make-create-section--advanced"
			showsStroke
		>
			<button
				type="button"
				className="make-create-section__advanced-toggle"
				aria-expanded={draft.isAdvancedExpanded}
				onClick={() =>
					onDraftChange({
						...draft,
						isAdvancedExpanded: !draft.isAdvancedExpanded,
					})
				}
			>
				<span>高级选项</span>
				<ArtistIcon
					name="chevron"
					size={16}
					style={{
						transform: draft.isAdvancedExpanded
							? "rotate(90deg)"
							: "rotate(0deg)",
						transition: "transform 200ms ease",
					}}
				/>
			</button>
			{draft.isAdvancedExpanded ? (
				<div className="make-create-section__advanced-body">
					<div className="make-create-section__vocal-group">
						<span className="make-create-section__vocal-label">人声性别</span>
						<UnderlineTabBar
							tabs={["none", "male", "female"] as const}
							selected={draft.vocalGender}
							onSelect={(value) =>
								onDraftChange({
									...draft,
									vocalGender: value as CreateVocalGender,
								})
							}
							label={(value) => VOCAL_GENDER_LABELS[value as CreateVocalGender]}
						/>
					</div>
					<div className="make-create-section__vocal-group">
						<div className="make-create-section__vocal-label-row">
							<span className="make-create-section__vocal-label">
								负面提示词
							</span>
							<span className="make-create-section__vocal-optional">可选</span>
						</div>
						<GlassTextEditor
							label="负面提示词"
							placeholder="不想出现的元素，例如：失真、噪声、口哨..."
							value={draft.negativePrompt}
							onChange={(event) =>
								onDraftChange({ ...draft, negativePrompt: event.target.value })
							}
						/>
					</div>
				</div>
			) : null}
		</GlassCard>
	);
}

// ─────────────────────────────────────────────────────────────────────────────
// Title Section
// ─────────────────────────────────────────────────────────────────────────────

interface TitleSectionProps {
	readonly draft: CreateComposerDraft;
	readonly onDraftChange: (next: CreateComposerDraft) => void;
}

function TitleSection({ draft, onDraftChange }: Readonly<TitleSectionProps>) {
	return (
		<GlassCard className="make-create-section">
			<header className="make-create-section__header">
				<h2>歌曲标题</h2>
				<span className="make-create-section__optional">可选</span>
			</header>
			<GlassTextField
				label="歌曲标题"
				placeholder="给这首歌起个名字..."
				value={draft.title}
				onChange={(event) =>
					onDraftChange({ ...draft, title: event.target.value })
				}
				autoComplete="off"
			/>
		</GlassCard>
	);
}

// ─────────────────────────────────────────────────────────────────────────────
// Model Selector Button + Sheet
// ─────────────────────────────────────────────────────────────────────────────

interface ModelSelectorButtonProps {
	readonly selectedModel: MakeModelDescriptor | undefined;
	readonly availableModels: readonly MakeModelDescriptor[];
	readonly onSelectModel: (model: MakeModelDescriptor) => void;
	readonly isSheetOpen: boolean;
	readonly onSheetToggle: () => void;
	readonly onSheetClose: () => void;
}

function ModelSelectorButton({
	selectedModel,
	availableModels,
	onSelectModel,
	isSheetOpen,
	onSheetToggle,
	onSheetClose,
}: Readonly<ModelSelectorButtonProps>) {
	const swatchStyle: CSSProperties = selectedModel
		? {
				display: "inline-block",
				width: "10px",
				height: "10px",
				borderRadius: "9999px",
				background: selectedModel.callToActionTint,
				boxShadow:
					"0 0 0 2px color-mix(in srgb, var(--ds-surface) 80%, transparent)",
			}
		: {};
	return (
		<div className="make-model-selector">
			<button
				type="button"
				className="make-model-selector__trigger"
				aria-haspopup="dialog"
				aria-expanded={isSheetOpen}
				onClick={onSheetToggle}
			>
				<span style={swatchStyle} aria-hidden="true" />
				<span>{selectedModel?.displayName ?? "选择模型"}</span>
				<ArtistIcon
					name="chevron"
					size={14}
					style={{ transform: "rotate(90deg)" }}
				/>
			</button>
			{isSheetOpen ? (
				<div
					role="dialog"
					aria-label="选择生成模型"
					className="make-model-selector__sheet"
					onClick={(event) => {
						if (event.target === event.currentTarget) onSheetClose();
					}}
					onKeyDown={(event) => {
						if (event.key === "Escape") onSheetClose();
					}}
				>
					<div className="make-model-selector__card">
						<header className="make-model-selector__header">
							<h3>选择模型</h3>
							<button
								type="button"
								className="make-model-selector__close"
								aria-label="关闭"
								onClick={onSheetClose}
							>
								<ArtistIcon name="close" size={18} strokeWidth={2.4} />
							</button>
						</header>
						<ul className="make-model-selector__list">
							{availableModels.map((model) => {
								const isSelected = selectedModel?.id === model.id;
								return (
									<li key={model.id}>
										<button
											type="button"
											className={
												isSelected
													? "make-model-selector__option make-model-selector__option--selected"
													: "make-model-selector__option"
											}
											onClick={() => {
												onSelectModel(model);
												onSheetClose();
											}}
										>
											<span
												className="make-model-selector__swatch"
												style={{ background: model.callToActionTint }}
												aria-hidden="true"
											/>
											<span className="make-model-selector__copy">
												<strong>{model.displayName}</strong>
												<small>
													{model.providerDisplayName} ·{" "}
													{model.musicGenerationPrice} 灵感 / 次
												</small>
											</span>
											{isSelected ? (
												<ArtistIcon name="check" size={18} />
											) : null}
										</button>
									</li>
								);
							})}
						</ul>
					</div>
				</div>
			) : null}
		</div>
	);
}

const visuallyHidden: CSSProperties = {
	position: "absolute",
	width: "1px",
	height: "1px",
	padding: 0,
	margin: "-1px",
	overflow: "hidden",
	clip: "rect(0,0,0,0)",
	whiteSpace: "nowrap",
	border: 0,
};
